import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  createTheme,
  Collapse,
  IconButton,
  Popover,
  Button,
  LinearProgress,
  Paper,
  Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { alpha } from "@mui/material/styles";
import {
  Build as BuildIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
  Category as CategoryIcon,
  AssignmentInd as AssignmentIndIcon,
  Timer as TimerIcon,
  Schedule as TotalTimeIcon,
  PeopleRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useHistory } from "react-router-dom";
import "./requestCard.css";

const RequestCard = ({ rows, isPlannedMaintenance, isAutonomousMaintenance, page, onCardClick }) => {
  const theme = useTheme();
  const { t } = useTranslation("translation");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "technician-popover" : undefined;
  const history = useHistory();
  const [expandedNote, setExpandedNote] = useState(false);

  const [anchorE, setAnchorE] = useState(null);

  const handleClickAI = (event) => {
    event.stopPropagation();
    setAnchorE(event.currentTarget);
  };

  const handleCloseAI = () => {
    setAnchorE(null);
  };

  const openAI = Boolean(anchorE);
  const idAI = open ? "ai-suggest-popover" : undefined;

  // Add global keyframes
  const globalStyles = {
    "@global": {
      "@keyframes progressBarAnimation": {
        "0%": { backgroundPosition: "100% 0" },
        "100%": { backgroundPosition: "-100% 0" },
      },
    },
  };

  // Memoize the first row for performance
  const row = useMemo(() => rows[0] || {}, [rows]);

  // Advanced status and progress calculation
  const statusAnalysis = useMemo(() => {
    const statusMap = {
      1: {
        label: t("StatusCreated"),
        color: theme.palette.warning.main,
        progress: 10,
        icon: <WarningIcon color="warning" />,
      },
      2: {
        label: t("StatusAssigned"),
        color: theme.palette.primary.main,
        progress: 30,
        icon: <InfoIcon color="primary" />,
      },
      3: {
        label: t("StatusStarted"),
        color: "#7e57c2",
        progress: 60,
        icon: <TimeIcon sx={{ color: "#7e57c2" }} />,
      },
      4: {
        label: t("StatusPaused"),
        color: theme.palette.secondary.main,
        progress: 45,
        icon: <WarningIcon color="secondary" />,
      },
      5: {
        label: t("StatusCompleted"),
        color: theme.palette.success.main,
        progress: 100,
        icon: <CheckCircleIcon color="success" />,
      },
      6: {
        label: t("StatusClosed"),
        color: theme.palette.success.dark,
        progress: 100,
        icon: <CheckCircleIcon color="success" />,
      },
      7: {
        label: t("StatusCancelled"),
        color: theme.palette.error.main,
        progress: 0,
        icon: <WarningIcon color="error" />,
      },
      8: {
        label: t("StatusApproved"),
        color: theme.palette.info.main,
        progress: 100,
        icon: <WarningIcon color="info" />,
      },
    };

    return (
      statusMap[row.taskStatus] || {
        label: "Unknown",
        color: theme.palette.grey[500],
        progress: 0,
        icon: <InfoIcon color="disabled" />,
      }
    );
  }, [row, theme]);

  // Utility function to convert minutes to human-readable format
  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null) return "";

    // Convert to days, hours, and minutes
    const days = Math.floor(minutes / (24 * 60));
    const remainingMinutesAfterDays = minutes % (24 * 60);

    const hours = Math.floor(remainingMinutesAfterDays / 60);
    const remainingMinutes = Math.floor(remainingMinutesAfterDays % 60);

    // Build the duration string
    const parts = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }

    if (hours > 0) {
      parts.push(`${hours}h`);
    }

    if (remainingMinutes > 0 || (days === 0 && hours === 0)) {
      parts.push(`${remainingMinutes}m`);
    }

    // Limit to 2 parts for readability
    return parts.slice(0, 2).join(" ");
  };

  // Time Color Function (assuming this is defined elsewhere or needs to be imported)
  const TimeColor = (row) => {
    if ((row.taskStatus === 5 && row.totalDurationMin < 10) || row.taskStatus === 6 || row.taskStatus === 7) {
      return "#9E9E9E";
    }
    if (row.taskStatus === 5 && row.totalDurationMin >= 10) {
      return "#D50000";
    } else {
      if (row.durationMin >= 30 && row.durationMin < 60) {
        return "#FFBD00";
      } else {
        if (row.durationMin >= 60) {
          return "#F94144";
        } else {
          return "#4CAF50";
        }
      }
    }
  };

  const createdOverdue = useMemo(() => {
    if (page === "breakdownList" && row?.taskStatus === 1 && row?.createdDate) {
      const createdMoment = moment(row.createdDate);
      const now = moment();
      return now.diff(createdMoment, "minutes") >= 10;
    }
    return false;
  }, [row, page]);

  // Time Handling Function
  const HandleTime = (row) => {
    if (!isPlannedMaintenance) {
      //status 5="Kapandı" 6="Tamamlandı" 7="İptal Edildi"
      let time;
      if (row.taskStatus === 5 || row.taskStatus === 6) {
        if (isAutonomousMaintenance === true) {
          time = Math.floor(row.durationMin) + " " + t("min");
        } else if (page === "breakdownList" || page === "breakdownDetail") {
          time = moment(row.createdDate).format("DD/MM HH:mm");
        } else {
          time = Math.floor(row.totalDurationMin) + " " + t("min");
        }
      } else {
        time = Math.floor(row.durationMin) + " " + t("min");
      }
      return [time, TimeColor(row)];
    } else {
      const now = moment(new Date());
      const pDate = moment(row.plannedDate);
      const diff = now.startOf("day").diff(pDate.startOf("day"), "days");

      let day;
      let color;
      if (diff === 0) {
        day = t("Today");
        color = "#5959d6";
      } else if (diff < 0) {
        day = -1 * diff + " " + t("Day");
        color = "#4CAF50";
      } else {
        day = diff + " " + t("Day");
        color = "#F94144";
      }

      return [day, color];
    }
  };

  // Breakdown Detail Page Navigation
  const BreakdownDetailPage = (id) => {
    if (page === "breakdownList") {
      history.push("breakdowndetail?id=" + id);
    } else if (page === "plannedmaintenance") {
      history.push("planned-maintenance-detail?id=" + id);
    } else if (page === "AutonomousMaintenance") {
      history.push("autonomous-maintenance-detail?id=" + id);
    }
  };

  // Detailed information component
  const DetailSection = ({ icon: Icon, label, value, tooltip }) => (
    <Tooltip title={tooltip || value} placement="top">
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          py: 0.1,
          px: -10,
          borderRadius: 1,
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Icon
          fontSize="small"
          sx={{
            color: theme.palette.text.primary,
            opacity: 0.9,
            fontSize: isMobile ? "0.75rem" : "1rem",
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: isMobile ? "150px" : "200px",
            fontSize: isMobile ? "0.55rem" : "0.75rem",
          }}
        >
          {label} {label ? ":" : ""} {value}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        minHeight: isMobile ? "180px" : "270px",
        border: `3px solid ${HandleTime(row)[1]}`,
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
        ...(isMobile && {
          borderRadius: 1,
          boxShadow: theme.shadows[1],
          border: `1px solid ${HandleTime(row)[1]}`,
        }),
        ...(createdOverdue && {
          animation: "pulseBorder 2s infinite",
          borderColor: theme.palette.error.main,
        }),
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: theme.shadows[3],
          ...(isMobile && {
            transform: "none",
            boxShadow: theme.shadows[2],
          }),
        },
      }}
      onClick={() => {
        if (history && page) {
          BreakdownDetailPage(row.id);
        } else if (typeof onCardClick === "function") {
          onCardClick(row);
        } else {
          console.warn("No navigation method available");
        }
      }}
    >
      <Grid
        container
        spacing={isMobile ? 0.5 : 1}
        sx={{
          p: isMobile ? 1 : 1.5,
          width: "100%",
          margin: 0,
        }}
      >
        {/* Status Header */}
        <Grid item xs={12} display="flex" flexDirection="row" justifyContent="space-between">
          <Box
            sx={{
              display: "flex",
              fontWeight: "bold",
              fontSize: "14px",
              px: 0.1,
              py: 0.1,
              position: "absolute",
              top: -2,
              left: 10,
              zIndex: 1,
              backgroundColor: `${statusAnalysis.color}10`,
            }}
          >
            {statusAnalysis.icon}
          </Box>
          <Box ml={1.5} flexGrow={1}>
            <Typography
              variant="subtitle1"
              sx={{
                color: statusAnalysis.color,
                fontWeight: "bold",
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              }}
            >
              {statusAnalysis.label}
            </Typography>
            <Box
              sx={{
                width: "220px",
                height: isMobile ? 4 : 6,
                borderRadius: 3,
                backgroundColor: alpha(statusAnalysis.color, 0.3),
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: `${statusAnalysis.progress}%`,
                  height: "100%",
                  ...([1, 2, 3, 4].includes(row.taskStatus)
                    ? {
                        // Yeni stil (geliştirilmiş animasyon)
                        background: `linear-gradient(120deg, 
                                                ${alpha(statusAnalysis.color, 0.6)} 25%, 
                                                ${alpha("#ffffff", 0.3)} 50%, 
                                                ${alpha(statusAnalysis.color, 0.6)} 75%)`,
                        backgroundSize: "200% 100%",
                        animation: "progressBarShine 3s ease-in-out infinite",
                        transition: "width 0.5s ease-in-out",
                        boxShadow: `0 0 5px ${alpha(statusAnalysis.color, 0.4)}`,
                        "&:hover": {
                          background: `linear-gradient(120deg, 
                                                  ${alpha(statusAnalysis.color, 0.8)} 25%, 
                                                  ${alpha("#ffffff", 0.4)} 50%, 
                                                  ${alpha(statusAnalysis.color, 0.8)} 75%)`,
                        },
                      }
                    : {
                        // Eski stil (klasik animasyon)
                        background: `linear-gradient(45deg, 
                                                ${alpha(statusAnalysis.color, 0.7)} 0%, 
                                                ${alpha(statusAnalysis.color, 0.9)} 50%, 
                                                ${alpha(statusAnalysis.color, 0.7)} 100%)`,
                        backgroundSize: "200% 100%",
                        animation: "progressBarAnimation 2s linear infinite",
                      }),
                  borderRadius: 3,
                }}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Box gap={0.5} display="flex" flexDirection="column">
              {row.id && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: "bold",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    fontSize: isMobile ? "0.5rem" : "0.625rem",
                  }}
                >
                  {t("Call")}: {row.id}
                </Typography>
              )}
              {row.maintenanceOrderId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: "bold",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    fontSize: isMobile ? "0.5rem" : "0.625rem",
                  }}
                >
                  {t("TaskNo")}: {row.maintenanceOrderId}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Equipment Details */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="center" // Metinleri içeren Box'ı merkeze almak için
            alignItems="center"
            mb={1}
            sx={{ position: "relative" }} // İkon için absolute positioning'i etkinleştirmek için
          >
            {page === "breakdownDetail" && [1, 2, 3, 4, 5, 8, 10, 11, 14].includes(row.plantId) && row.aiSuggestion && (
              <>
                <Button
                  align="left"
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1, textTransform: "none" }} // 👈 Burada
                  onClick={handleClickAI}
                >
                  {t("SmartSuggestion")}
                </Button>

                <Popover
                  id={idAI}
                  open={openAI}
                  anchorEl={anchorE}
                  onClose={handleCloseAI}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box sx={{ p: 2, maxWidth: 380 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                      <TipsAndUpdatesIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        {t("AISuggestions")}
                      </Typography>
                    </Stack>

                    {/* Öneri 1 */}
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {t("Suggestion1")}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {row.aiSuggestion?.primarySuggestion?.text}
                      </Typography>

                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t("AccuracyScore")}: {row.aiSuggestion?.primarySuggestion?.score?.toFixed(2)}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={73}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            mt: 0.5,
                          }}
                        />
                      </Box>
                    </Paper>

                    {/* Öneri 2 */}
                    {row.aiSuggestion.combinedScore != 0 && (
                      <>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            {t("Suggestion2")}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                            {row.aiSuggestion?.secondarySuggestion?.text}
                          </Typography>

                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t("AccuracyScore")}: {row.aiSuggestion?.secondarySuggestion?.score?.toFixed(2)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={68}
                              color="primary"
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                mt: 0.5,
                              }}
                            />
                          </Box>
                        </Paper>
                      </>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Genel skor */}
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                      <CheckCircleOutlineIcon color="success" />
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "bold",
                          color: "success.main",
                        }}
                      >
                        {t("OverallAccuracyScore")}: {" "}
                        {row.aiSuggestion?.combinedScore == 0
                          ? row.aiSuggestion?.primarySuggestion?.score?.toFixed(2)
                          : row.aiSuggestion?.combinedScore?.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Box>
                </Popover>
              </>
            )}

            <Box width="calc(100% - 120px)">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                  fontSize: isMobile ? "0.55rem" : "1rem",
                }}
              >
                {row.equipmentName || t("NoEquipment")}
              </Typography>
              {row.equipmentCode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: isMobile ? "0.55rem" : "0.675rem",
                  }}
                >
                  {row.equipmentCode}
                </Typography>
              )}
            </Box>
            {row.techList && row.technicianCount > 1 && (
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  position: "absolute", // Ana Box'a göre konumlandır
                  right: 0, // En sağa yasla
                  py: 0.1,
                  px: 1,
                  borderRadius: 1,
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  cursor: "pointer", // Tıklanabilir olduğunu belirtmek için
                }}
                onClick={handleClick} // Tıklama olayını yakala
              >
                <PeopleRounded
                  fontSize="large"
                  sx={{
                    color: theme.palette.text.primary,
                    opacity: 0.9,
                    fontSize: isMobile ? "1rem" : "1.25rem",
                  }}
                />
              </Box>
            )}
            <Popover
              id={"tech-popover"}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("AssigneeList")}
                </Typography>
                {row.techList?.map((tech) => (
                  <Typography key={tech.userName} variant="body2">
                    {tech.userName}
                  </Typography>
                ))}
              </Box>
            </Popover>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        {/* Detailed Information */}
        <Grid item xs={12} container spacing={isMobile ? 0.5 : 1}>
          <Grid item xs={6}>
            {row.poolName && <DetailSection icon={LocationOnIcon} label={t("Pool")} value={row.poolName} />}
            <DetailSection icon={CategoryIcon} label={t("BreakdownGroup")} value={row.breakdownReasonGroupName} />
            {row.maintenanceTypeName && (
              <DetailSection icon={BuildIcon} label={t("MaintenanceType")} value={row.maintenanceTypeName} />
            )}
          </Grid>
          <Grid item xs={6}>
            {row.creatorName && <DetailSection icon={PersonIcon} label={t("Creator")} value={row.creatorName} />}
            {row.status && (
              <DetailSection icon={statusAnalysis.icon} label={t("Status")} value={statusAnalysis.label} />
            )}
            {(row.taskStatus === 2 ||
              row.taskStatus === 3 ||
              row.taskStatus === 4 ||
              row.taskStatus === 5 ||
              row.taskStatus === 6) &&
              row.assigneeName && (
                <DetailSection icon={AssignmentIndIcon} label={t("Assigned")} value={row.assigneeName} />
              )}

            {(isPlannedMaintenance || isAutonomousMaintenance) && row.plannedDate && (
              <DetailSection
                icon={TimeIcon}
                label={t("PlannedDate")}
                value={moment(row.plannedDate).format("DD.MM.YYYY")}
              />
            )}
          </Grid>
        </Grid>

        {/* Timestamps and Additional Info */}
        {row.completedDate && (row.taskStatus === 5 || row.taskStatus === 6) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" flexDirection="column" flexWrap="wrap">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? "0.5rem" : "0.625rem",
                }}
              >
                <strong>{t("CompletedDate")}:</strong> {moment(row.completedDate).fromNow()}
              </Typography>

              {row.createdDate && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: isMobile ? "0.5rem" : "0.625rem",
                    mt: 0.5, // Üstüne biraz boşluk ekler
                  }}
                >
                  <strong>{t("CreatedDate")}:</strong> {moment(row.createdDate).format("DD/MM/YYYY HH:mm")}
                </Typography>
              )}
            </Box>
          </Grid>
        )}

        {/* Footer with Duration and Priority */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box
              display="flex"
              alignItems="center"
              flexGrow={1}
              pr={1}
              sx={{
                minWidth: 0, // Ensures flex item can shrink below content size
                maxWidth: "100%",
                textAlign: "left", // Ensure left alignment
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? "0.52rem" : "0.68rem",
                  fontStyle: "italic",
                  flexGrow: 1,
                  minWidth: 0, // Allows text to shrink
                  width: "100%",
                  textAlign: "left", // Ensure left alignment
                  display: "flex", // Use flex to keep title and note inline
                  alignItems: "center", // Vertically align items
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: "bold",
                    mr: 0.5,
                    fontSize: "inherit",
                    textAlign: "left", // Ensure left alignment
                    whiteSpace: "nowrap", // Prevent title from wrapping
                  }}
                >
                  {t("OrderNote")}:
                </Typography>
                {row.orderNote ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      width: "100%",
                      textAlign: "left", // Ensure left alignment
                      overflow: "hidden", // Prevent overflow
                    }}
                  >
                    <Typography
                      variant="inherit"
                      sx={{
                        display: "inline-block",
                        width: "100%",
                        fontSize: "inherit",
                        textAlign: "left",
                        ml: 0.5,
                        whiteSpace: "normal", // Satır sonlarında normal taşsın
                        wordBreak: "break-word", // Kelime uzun olursa bölsün
                      }}
                    >
                      {row.orderNote}
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    variant="inherit"
                    sx={{
                      display: "inline-block",
                      width: "100%",
                      fontSize: "inherit",
                      textAlign: "left",
                      ml: 0.5,
                    }}
                  >
                    {/* Boşsa boş göster */}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              {row.durationMin !== undefined && row.taskStatus !== 5 && (
                <Chip
                  icon={<TimerIcon />}
                  label={formatDuration(row.durationMin)}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    fontSize: isMobile ? "0.625rem" : "0.75rem",
                    height: isMobile ? 18 : 32,
                    "& .MuiChip-icon": {
                      fontSize: isMobile ? "0.75rem" : "1rem",
                      marginLeft: "4px",
                    },
                  }}
                />
              )}
              {row.totalDurationMin !== undefined && (
                <Chip
                  icon={<TotalTimeIcon />}
                  label={formatDuration(row.totalDurationMin)}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    fontSize: isMobile ? "0.625rem" : "0.75rem",
                    height: isMobile ? 18 : 32,
                    "& .MuiChip-icon": {
                      fontSize: isMobile ? "0.75rem" : "1rem",
                      marginLeft: "4px",
                    },
                  }}
                />
              )}

              {row.priority && (
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    border: `1px solid ${
                      row.priority >= 3
                        ? theme.palette.error.light
                        : row.priority === 2
                          ? theme.palette.warning.light
                          : theme.palette.success.light
                    }`,
                    borderRadius: "50%",
                    width: isMobile ? "16px" : "20px",
                    height: isMobile ? "16px" : "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor:
                      row.priority >= 3
                        ? alpha(theme.palette.error.light, 0.2)
                        : row.priority === 2
                          ? alpha(theme.palette.warning.light, 0.2)
                          : alpha(theme.palette.success.light, 0.2),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: isMobile ? "0.5rem" : "0.625rem",
                      fontWeight: "bold",
                      color:
                        row.priority >= 3
                          ? theme.palette.error.main
                          : row.priority === 2
                            ? theme.palette.warning.main
                            : theme.palette.success.main,
                    }}
                  >
                    {row.priority}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestCard;
